/**
 * Tutorial Progress Tracking Utility
 * Stores and retrieves tutorial progress from localStorage
 */

const TUTORIAL_STORAGE_KEY = 'flashback_tutorial_progress';

export interface TutorialStepProgress {
  stepId: string;
  completed: boolean;
  completedAt?: string;
}

export interface TutorialProgress {
  tutorialId: string;
  currentStep: number;
  steps: TutorialStepProgress[];
  started: boolean;
  startedAt?: string;
  completed: boolean;
  completedAt?: string;
  dismissed: boolean;
  dismissedAt?: string;
}

export interface AllTutorialProgress {
  tutorials: Record<string, TutorialProgress>;
  lastUpdated: string;
}

/**
 * Get all tutorial progress from localStorage
 */
export const getAllTutorialProgress = (): AllTutorialProgress => {
  try {
    const stored = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to parse tutorial progress from localStorage:', e);
  }
  return {
    tutorials: {},
    lastUpdated: new Date().toISOString(),
  };
};

/**
 * Save all tutorial progress to localStorage
 */
const saveAllTutorialProgress = (progress: AllTutorialProgress): void => {
  try {
    progress.lastUpdated = new Date().toISOString();
    localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(progress));
  } catch (e) {
    console.error('Failed to save tutorial progress to localStorage:', e);
  }
};

/**
 * Get progress for a specific tutorial
 */
export const getTutorialProgress = (tutorialId: string): TutorialProgress | null => {
  const allProgress = getAllTutorialProgress();
  return allProgress.tutorials[tutorialId] || null;
};

/**
 * Initialize or get tutorial progress
 */
export const initTutorialProgress = (tutorialId: string, stepIds: string[]): TutorialProgress => {
  const existing = getTutorialProgress(tutorialId);
  if (existing) {
    return existing;
  }

  const newProgress: TutorialProgress = {
    tutorialId,
    currentStep: 0,
    steps: stepIds.map((stepId) => ({
      stepId,
      completed: false,
    })),
    started: false,
    completed: false,
    dismissed: false,
  };

  const allProgress = getAllTutorialProgress();
  allProgress.tutorials[tutorialId] = newProgress;
  saveAllTutorialProgress(allProgress);

  return newProgress;
};

/**
 * Mark a tutorial as started
 */
export const startTutorial = (tutorialId: string): void => {
  const allProgress = getAllTutorialProgress();
  if (allProgress.tutorials[tutorialId]) {
    allProgress.tutorials[tutorialId].started = true;
    allProgress.tutorials[tutorialId].startedAt = new Date().toISOString();
    allProgress.tutorials[tutorialId].dismissed = false;
    saveAllTutorialProgress(allProgress);
  }
};

/**
 * Update current step of a tutorial
 */
export const setTutorialStep = (tutorialId: string, stepIndex: number): void => {
  const allProgress = getAllTutorialProgress();
  if (allProgress.tutorials[tutorialId]) {
    allProgress.tutorials[tutorialId].currentStep = stepIndex;
    saveAllTutorialProgress(allProgress);
  }
};

/**
 * Mark a step as completed
 */
export const completeStep = (tutorialId: string, stepId: string): void => {
  const allProgress = getAllTutorialProgress();
  if (allProgress.tutorials[tutorialId]) {
    const step = allProgress.tutorials[tutorialId].steps.find((s) => s.stepId === stepId);
    if (step) {
      step.completed = true;
      step.completedAt = new Date().toISOString();
      saveAllTutorialProgress(allProgress);
    }
  }
};

/**
 * Mark a tutorial as completed
 */
export const completeTutorial = (tutorialId: string): void => {
  const allProgress = getAllTutorialProgress();
  if (allProgress.tutorials[tutorialId]) {
    allProgress.tutorials[tutorialId].completed = true;
    allProgress.tutorials[tutorialId].completedAt = new Date().toISOString();
    // Mark all steps as completed
    allProgress.tutorials[tutorialId].steps.forEach((step) => {
      step.completed = true;
      if (!step.completedAt) {
        step.completedAt = new Date().toISOString();
      }
    });
    saveAllTutorialProgress(allProgress);
  }
};

/**
 * Dismiss a tutorial (user closed it without completing)
 */
export const dismissTutorial = (tutorialId: string): void => {
  const allProgress = getAllTutorialProgress();
  if (allProgress.tutorials[tutorialId]) {
    allProgress.tutorials[tutorialId].dismissed = true;
    allProgress.tutorials[tutorialId].dismissedAt = new Date().toISOString();
    saveAllTutorialProgress(allProgress);
  }
};

/**
 * Reset a tutorial progress
 */
export const resetTutorial = (tutorialId: string): void => {
  const allProgress = getAllTutorialProgress();
  if (allProgress.tutorials[tutorialId]) {
    allProgress.tutorials[tutorialId].currentStep = 0;
    allProgress.tutorials[tutorialId].started = false;
    allProgress.tutorials[tutorialId].startedAt = undefined;
    allProgress.tutorials[tutorialId].completed = false;
    allProgress.tutorials[tutorialId].completedAt = undefined;
    allProgress.tutorials[tutorialId].dismissed = false;
    allProgress.tutorials[tutorialId].dismissedAt = undefined;
    allProgress.tutorials[tutorialId].steps.forEach((step) => {
      step.completed = false;
      step.completedAt = undefined;
    });
    saveAllTutorialProgress(allProgress);
  }
};

/**
 * Reset all tutorial progress
 */
export const resetAllTutorials = (): void => {
  localStorage.removeItem(TUTORIAL_STORAGE_KEY);
};

/**
 * Check if a tutorial has been completed
 */
export const isTutorialCompleted = (tutorialId: string): boolean => {
  const progress = getTutorialProgress(tutorialId);
  return progress?.completed || false;
};

/**
 * Check if a tutorial has been dismissed
 */
export const isTutorialDismissed = (tutorialId: string): boolean => {
  const progress = getTutorialProgress(tutorialId);
  return progress?.dismissed || false;
};

/**
 * Check if a step has been completed
 */
export const isStepCompleted = (tutorialId: string, stepId: string): boolean => {
  const progress = getTutorialProgress(tutorialId);
  if (!progress) return false;
  const step = progress.steps.find((s) => s.stepId === stepId);
  return step?.completed || false;
};

/**
 * Get the number of completed steps for a tutorial
 */
export const getCompletedStepsCount = (tutorialId: string): number => {
  const progress = getTutorialProgress(tutorialId);
  if (!progress) return 0;
  return progress.steps.filter((s) => s.completed).length;
};

/**
 * Get tutorial completion percentage
 */
export const getTutorialCompletionPercentage = (tutorialId: string): number => {
  const progress = getTutorialProgress(tutorialId);
  if (!progress || progress.steps.length === 0) return 0;
  const completed = progress.steps.filter((s) => s.completed).length;
  return Math.round((completed / progress.steps.length) * 100);
};

// Tutorial IDs for reference
export const TUTORIAL_IDS = {
  WELCOME: 'welcome-tutorial',
  CLOUD_GATEWAY: 'cloud-gateway-tutorial',
  ONBOARDING: 'onboarding-tutorial',
  ACCOUNT_SETTINGS: 'account-settings-tutorial',
  GATEWAY: 'gateway-tutorial',
  PRIVATE_CHAT: 'private-chat-tutorial',
} as const;
